<script setup lang="ts">
import type { ITableHeader } from "@/components/ui/Table/table.interface"
import type { EventTemplate } from "@/models"
import type { ToastHtmlElement } from "@/types"
import { useMutation, useQuery, useQueryClient } from "@tanstack/vue-query"
import { computed, ref } from "vue"
import { applyEventTemplate, clearEventTemplate, listEventTemplates } from "@/api"
import { AppLink, ButtonLink, NoData, Table, Toast } from "@/components/ui"
import { useConvertedRouteParam, useTranslation } from "@/composables"
import { SidebarHostEventLayout } from "@/layouts"

definePage({
  meta: {
    requiresAuth: true,
  },
})

const eventVaultId = useConvertedRouteParam("id", "/event/[id]/template/apply", value => Number(value))
const errorToast = ref<ToastHtmlElement | null>(null)
const successToast = ref<ToastHtmlElement | null>(null)

const { d, t } = useTranslation()

const { isLoading: isLoadingTemplates, data: eventTemplates } = useQuery({
  queryKey: ["listEventTemplates", eventVaultId],
  queryFn: () => listEventTemplates(eventVaultId.value),
  initialData: [],
})

const client = useQueryClient()

const invalidateTemplateList = async () => await client.invalidateQueries({ queryKey: ["listEventTemplates", eventVaultId] })

function showErrorToast() {
  return errorToast.value?.showToast({ title: t("something_went_wrong"), content: t("try_again"), icon: "Caution" })
}
function showSuccesToast(message: string) {
  return successToast.value?.showToast({ title: t("success"), content: message, icon: "CircleCheck" })
}

const { mutate: applyTemplate, isPending: isAplyingTemplate } = useMutation({
  mutationFn: (templateId?: number) => applyEventTemplate(templateId, eventVaultId.value),
  onSuccess: async () => {
    invalidateTemplateList()
    showSuccesToast(t("event_templates_list_page.success_apply"))
  },
  onError: () => showErrorToast(),
})

const { mutate: removeTemplate, isPending: isRemovingTemplate } = useMutation({
  mutationFn: (templateId?: number) => clearEventTemplate(templateId, eventVaultId.value),
  onSuccess: async () => {
    invalidateTemplateList()
    showSuccesToast(t("event_templates_list_page.success_clear"))
  },
  onError: () => showErrorToast(),
})

const isLoading = computed(() => isLoadingTemplates.value || isAplyingTemplate.value || isRemovingTemplate.value)

const headers: ITableHeader<EventTemplate & { actions?: unknown }>[] = [
  { label: t("event_templates_list_page.header.name"), value: "name" },
  { label: t("event_templates_list_page.header.last_updated"), value: "lastUpdated" },
  { label: t("event_templates_list_page.header.action"), value: "actions" },
]
</script>

<template>
  <SidebarHostEventLayout>
    <div class="flex flex-col gap-6 bg-white">
      <div class="flex justify-between gap-4 items-center flex-wrap">
        <h1 class="font-comfortaa font-bold text-xl text-[#072A47]">
          {{ t("event_templates_list_page.title") }}
        </h1>
        <ButtonLink to="/template/create" prefix-icon="CirclePlus">
          <span class="sr-only md:not-sr-only">
            {{ t("event_templates_list_page.new_button") }}
          </span>
        </ButtonLink>
      </div>

      <Table :headers="headers" :data="eventTemplates as Array<EventTemplate & { actions?: unknown }>" :is-loading="isLoading" compact>
        <template #empty>
          <tbody>
            <tr>
              <td :colspan="headers.length + 1">
                <NoData
                  :headline="t('event_templates_list_page.no_template_headline')"
                  :content="t('event_templates_list_page.no_template_content')"
                  hide-button
                />
              </td>
            </tr>
          </tbody>
        </template>

        <template #data-name="{ data }">
          <div class="font-semibold font-montserrat">
            {{ data?.name ? data?.name : "—" }}
          </div>
          <div v-if="data.isApplied" class="text-sm italic text-gray-500">
            {{ t("event_templates_list_page.applied_label") }}
          </div>
        </template>

        <template #data-lastUpdated="{ data }">
          <span v-if="data.lastUpdated">{{ d(data.lastUpdated, "short") }}</span>
        </template>

        <template #data-actions="{ data }">
          <div class="flex items-center gap-2">
            <button
              v-if="data.isApplied" class="underline hover:text-link text-red-700"
              @click="() => removeTemplate(data.id)"
            >
              {{ t("event_templates_list_page.cancel_btn") }}
            </button>
            <button
              v-else class="underline hover:text-link"
              @click="() => applyTemplate(data.id)"
            >
              {{ t("event_templates_list_page.apply_btn") }}
            </button>
            <div>|</div>
            <AppLink :to="`/template/${data.id}/edit`" class="underline hover:text-link">
              {{ t("event_templates_list_page.edit_btn") }}
            </AppLink>
          </div>
        </template>
      </Table>
    </div>
  </SidebarHostEventLayout>

  <Toast ref="successToast" variant="success" />
  <Toast ref="errorToast" variant="danger" />
</template>
